import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { X, Download, ZoomIn, ZoomOut, RotateCw, Maximize2, FileText, Image, File } from 'lucide-react';

const FilePreview = ({ file, open, onOpenChange, downloadUrl }) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  const isPDF = file?.type_mime === 'application/pdf' || file?.nom_fichier_original?.endsWith('.pdf');
  const isImage = file?.type_mime?.startsWith('image/') || 
    /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file?.nom_fichier_original || '');

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => { setZoom(100); setRotation(0); };

  const renderPreview = () => {
    if (!file || !downloadUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <File className="w-16 h-16 mb-4" />
          <p>Impossible de charger la prévisualisation</p>
        </div>
      );
    }

    if (isPDF) {
      return (
        <iframe
          src={`${downloadUrl}#toolbar=1&navpanes=0`}
          className="w-full h-[70vh] border-0 rounded-lg"
          title={file.nom_fichier_original}
        />
      );
    }

    if (isImage) {
      return (
        <div className="flex items-center justify-center overflow-auto p-4 bg-slate-100 dark:bg-slate-800 rounded-lg min-h-[400px]">
          <img
            src={downloadUrl}
            alt={file.nom_fichier_original}
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease',
              maxWidth: '100%',
              maxHeight: '70vh',
            }}
            className="object-contain"
          />
        </div>
      );
    }

    // Unsupported file type
    return (
      <div className="flex flex-col items-center justify-center h-96 text-slate-400">
        <FileText className="w-16 h-16 mb-4" />
        <p className="text-lg font-medium mb-2">Prévisualisation non disponible</p>
        <p className="text-sm mb-4">Ce type de fichier ne peut pas être prévisualisé</p>
        <Button onClick={() => window.open(downloadUrl, '_blank')}>
          <Download className="w-4 h-4 mr-2" />
          Télécharger le fichier
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0">
        <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            {isPDF ? (
              <FileText className="w-5 h-5 text-red-500" />
            ) : isImage ? (
              <Image className="w-5 h-5 text-blue-500" />
            ) : (
              <File className="w-5 h-5 text-slate-500" />
            )}
            <DialogTitle className="text-lg truncate max-w-md">
              {file?.nom_fichier_original || 'Fichier'}
            </DialogTitle>
          </div>
          
          <div className="flex items-center gap-2">
            {isImage && (
              <>
                <Button variant="ghost" size="sm" onClick={handleZoomOut} title="Zoom -">
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500 w-12 text-center">{zoom}%</span>
                <Button variant="ghost" size="sm" onClick={handleZoomIn} title="Zoom +">
                  <ZoomIn className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleRotate} title="Rotation">
                  <RotateCw className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleReset} title="Réinitialiser">
                  <Maximize2 className="w-4 h-4" />
                </Button>
                <div className="w-px h-6 bg-slate-200 mx-1" />
              </>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.open(downloadUrl, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </div>
        </DialogHeader>
        
        <div className="p-4 overflow-auto">
          {renderPreview()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { FilePreview };
